import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, StageChip, Stat, Empty } from "../../dashboard/primitives";
import {
  subscribeAllRequests,
  setRequestStatus,
  acceptRequest,
} from "../../requests/api";
import { subscribeAllUsers } from "../../projects/api";
import { DISCIPLINE_LABELS, timelineLabel } from "../../projects/pricing";
import { SkeletonList, SkeletonRow } from "../../components/Skeleton";
import { useMinLoading } from "../../lib/useMinLoading";

const REQUEST_STATUSES = [
  "submitted",
  "reviewing",
  "quoted",
  "accepted",
  "declined",
];

const TYPE_LABELS = {
  web: "Web app",
  shop: "E-commerce",
  mobile: "Mobile app",
  backend: "Backend work",
  brand: "Brand + site",
};

const CLOSED = new Set(["accepted", "declined", "closed", "archived"]);

const fmt = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);

const fmtWhen = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

const typeLabel = (r) => {
  const list = r.types?.length ? r.types : r.type ? [r.type] : [];
  if (!list.length) return "Request";
  return list.map((t) => TYPE_LABELS[t] || t).join(", ");
};

const disciplineLabel = (r) => {
  const list = r.disciplines || [];
  if (!list.length) return null;
  return list.map((d) => DISCIPLINE_LABELS[d] || d).join(" + ");
};

const deriveTitle = (r) => {
  const fromNotes = (r.notes || "").trim().slice(0, 60);
  if (fromNotes) return fromNotes;
  const fromScope = (r.scope || []).join(", ");
  if (fromScope) return fromScope;
  return typeLabel(r);
};

const RequestsSection = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [users, setUsers] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [showClosed, setShowClosed] = useState(false);

  useEffect(
    () =>
      subscribeAllRequests((v) => {
        setRequests(v);
        setLoaded(true);
      }),
    []
  );
  useEffect(() => subscribeAllUsers(setUsers), []);

  const showSkeleton = useMinLoading(!loaded);

  const usersByUid = useMemo(
    () => Object.fromEntries(users.map((u) => [u.uid || u.id, u])),
    [users]
  );

  const stats = useMemo(() => {
    const open = requests.filter(
      (r) => !CLOSED.has((r.status || "submitted").toLowerCase())
    ).length;
    const accepted = requests.filter((r) => r.status === "accepted").length;
    const pipeline = requests
      .filter((r) => !CLOSED.has((r.status || "submitted").toLowerCase()))
      .reduce((n, r) => n + (r.budgetUsd || 0), 0);
    return { total: requests.length, open, accepted, pipeline };
  }, [requests]);

  const visible = useMemo(
    () =>
      requests.filter((r) =>
        showClosed
          ? CLOSED.has((r.status || "").toLowerCase())
          : !CLOSED.has((r.status || "submitted").toLowerCase())
      ),
    [requests, showClosed]
  );

  const onAccept = async (r) => {
    setError(null);
    if (r.projectId) {
      navigate("/admin/projects");
      return;
    }
    const title = window.prompt("Project title", deriveTitle(r));
    if (title === null) return; // cancelled
    setBusyId(r.id);
    try {
      await acceptRequest({
        requestId: r.id,
        title: title.trim() || deriveTitle(r),
        summary: r.notes || "",
      });
      navigate("/admin/projects");
    } catch (e) {
      setError(e?.message || "Could not accept request");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="fw-stat-grid">
        <Stat label="Requests" value={stats.total} loading={showSkeleton} />
        <Stat label="Open" value={stats.open} loading={showSkeleton} />
        <Stat label="Accepted" value={stats.accepted} loading={showSkeleton} />
        <Stat
          label="Open pipeline"
          value={fmt(stats.pipeline)}
          loading={showSkeleton}
        />
      </div>

      <Card
        title={showClosed ? "Closed requests" : "Incoming requests"}
        action={
          <button
            className="fw-btn fw-btn--ghost"
            onClick={() => setShowClosed((v) => !v)}
          >
            {showClosed ? "Show open" : "Show closed"}
          </button>
        }
      >
        {error && (
          <div
            style={{
              marginBottom: 12,
              fontSize: 12,
              color: "var(--fw-red)",
              fontFamily: "var(--fw-mono)",
            }}
          >
            {error}
          </div>
        )}

        {showSkeleton ? (
          <SkeletonList count={3} ariaLabel="Loading requests">
            <SkeletonRow cols={4} widths={["1fr", "100px", "120px", "160px"]} />
          </SkeletonList>
        ) : visible.length === 0 ? (
          <Empty>
            {showClosed ? "No closed requests" : "Inbox zero — no open requests"}
          </Empty>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {visible.map((r) => {
              const owner = usersByUid[r.createdByUid || r.ownerUid];
              const disc = disciplineLabel(r);
              const rush = r.rushMultiplier > 1;
              return (
                <div
                  key={r.id}
                  style={{
                    border: "1px solid var(--fw-line)",
                    borderRadius: "var(--fw-r-md)",
                    padding: 16,
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto auto",
                      gap: 12,
                      alignItems: "center",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>
                        {typeLabel(r)}
                      </div>
                      <div
                        style={{
                          fontFamily: "var(--fw-mono)",
                          fontSize: 11,
                          color: "var(--fw-muted)",
                          textTransform: "uppercase",
                          letterSpacing: 1.2,
                          marginTop: 2,
                        }}
                      >
                        {owner?.displayName ||
                          owner?.email ||
                          (r.createdByUid || r.ownerUid)?.slice(0, 8)}{" "}
                        · {fmt(r.budgetUsd)}
                        {r.timeline
                          ? ` · ${timelineLabel(r.timeline)}`
                          : ""}
                        {r.paymentPlan ? ` · ${r.paymentPlan}` : ""}
                        {rush ? " · +25% rush" : ""}
                      </div>
                    </div>
                    <StageChip status={r.status || "submitted"} />
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        alignItems: "center",
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                      }}
                    >
                      <select
                        className="fw-select"
                        style={{ width: "auto", fontSize: 12, padding: "4px 8px" }}
                        value={r.status || "submitted"}
                        onChange={(e) => setRequestStatus(r.id, e.target.value)}
                      >
                        {REQUEST_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                      <button
                        className="fw-btn fw-btn--primary"
                        style={{ padding: "6px 12px", fontSize: 12 }}
                        disabled={busyId === r.id}
                        onClick={() => onAccept(r)}
                      >
                        {busyId === r.id
                          ? "Working…"
                          : r.projectId
                          ? "Open project →"
                          : "Accept & create project"}
                      </button>
                    </div>
                  </div>

                  {disc && (
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--fw-muted)",
                        marginTop: 10,
                      }}
                    >
                      <strong style={{ color: "var(--fw-ink)" }}>Work:</strong>{" "}
                      {disc}
                    </div>
                  )}

                  {r.scope?.length > 0 && (
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--fw-muted)",
                        marginTop: 6,
                      }}
                    >
                      <strong style={{ color: "var(--fw-ink)" }}>Scope:</strong>{" "}
                      {r.scope.join(", ")}
                    </div>
                  )}

                  {r.notes && (
                    <p
                      style={{
                        color: "var(--fw-muted)",
                        fontSize: 13,
                        margin: "10px 0 0",
                      }}
                    >
                      {r.notes}
                    </p>
                  )}

                  <div
                    style={{
                      fontFamily: "var(--fw-mono)",
                      fontSize: 11,
                      color: "var(--fw-muted)",
                      marginTop: 10,
                    }}
                  >
                    {fmtWhen(r.createdAt)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
};

export default RequestsSection;
